/*
 * Monthly strike-days timeline (stacked column).
 *
 * Two things distinguish it from treemap.js:
 *   - Data is embedded in the page, not fetched. The series never change within the cache hour.
 *   - Rendering is deferred until the chart is nearly in view. On the homepage it sits below a
 *     ~950px calendar, so on mobile it does no work during the initial render at all.
 *
 * Colours come from the theme's per-sector CSS classes rather than a palette defined here, so a
 * sector is the same colour in the chart, the calendar and the badges. Highcharts needs a real
 * colour value, so each class is resolved once against a throwaway element.
 */
(function () {
    'use strict';

    var WRAPPER = '.js-strike-timeline';
    var FALLBACK_COLOUR = '#6c757d';

    var probeHost = null;

    function sectorColour(cls) {
        if (!cls) return FALLBACK_COLOUR;

        if (!probeHost) {
            probeHost = document.createElement('div');
            probeHost.style.cssText = 'position:absolute;left:-9999px;top:0;width:0;height:0;overflow:hidden';
            document.body.appendChild(probeHost);
        }

        var el = document.createElement('span');
        el.className = cls;
        probeHost.appendChild(el);
        var colour = window.getComputedStyle(el).backgroundColor;
        probeHost.removeChild(el);

        // A class that purgecss stripped, or one that was never defined, computes as transparent.
        if (!colour || colour === 'transparent' || colour === 'rgba(0, 0, 0, 0)') {
            return FALLBACK_COLOUR;
        }
        return colour;
    }

    function render(wrap) {
        var canvas = wrap.querySelector('.strike-timeline-canvas');
        var payload = wrap.querySelector('.js-strike-timeline-data');
        if (!canvas || !payload) return;

        var data;
        try {
            data = JSON.parse(payload.textContent);
        } catch (e) {
            console.error('Failed to parse strike timeline data:', e);
            wrap.style.display = 'none';
            return;
        }

        // Hide the panel rather than drawing an empty set of axes.
        if (!data.categories || !data.categories.length || !data.series || !data.series.length) {
            wrap.style.display = 'none';
            return;
        }

        Highcharts.chart(canvas, {
            chart: {
                type: 'column',
                backgroundColor: 'transparent',
                height: 420,
                // Narrow screens scroll via the wrapper's own overflow-x plus a min-width on the
                // canvas (see high-chart-overrides.scss) rather than Highcharts'
                // chart.scrollablePlotArea — that option builds its scrolling element here but
                // never widens the inner container, so 50 months just get crushed to ~5px each.
                style: {
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                }
            },
            title: { text: null },
            xAxis: {
                categories: data.categories,
                tickInterval: 3,
                labels: { rotation: -45, style: { fontSize: '11px' } }
            },
            yAxis: {
                min: 0,
                title: { text: 'Strike days' },
                stackLabels: { enabled: false }
            },
            plotOptions: {
                column: {
                    stacking: 'normal',
                    borderWidth: 0,
                    groupPadding: 0.05,
                    pointPadding: 0.02
                }
            },
            tooltip: {
                shared: true,
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                borderWidth: 0,
                borderRadius: 10,
                style: { color: '#ffffff' },
                headerFormat: '<span style="font-size:13px;font-weight:700">{point.key}</span><br/>',
                pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
                footerFormat: '<span style="font-size:12px;color:#d1d5db">Total: {point.total} strike days</span>'
            },
            legend: { enabled: true, itemStyle: { fontSize: '12px' } },
            credits: { enabled: false },
            series: data.series.map(function (s) {
                return {
                    name: s.name,
                    data: s.data,
                    color: sectorColour(s.colorClass)
                };
            }),
            responsive: {
                rules: [{
                    condition: { maxWidth: 768 },
                    chartOptions: {
                        chart: { height: 340 },
                        legend: { enabled: false },
                        xAxis: { tickInterval: 6 }
                    }
                }]
            }
        });

        // Where the wrapper is scrolling (narrow screens), open on the most recent months —
        // "what is happening now" is the reason anyone looks at this.
        if (wrap.scrollWidth > wrap.clientWidth) {
            wrap.scrollLeft = wrap.scrollWidth;
        }
    }

    document.addEventListener('DOMContentLoaded', function () {
        var wraps = document.querySelectorAll(WRAPPER);
        if (!wraps.length || typeof Highcharts === 'undefined') return;

        if (!('IntersectionObserver' in window)) {
            Array.prototype.forEach.call(wraps, render);
            return;
        }

        var observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;
                observer.unobserve(entry.target);
                render(entry.target);
            });
        }, { rootMargin: '250px 0px' });

        Array.prototype.forEach.call(wraps, function (wrap) {
            observer.observe(wrap);
        });
    });
})();
